import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../providers/DataProvider';
import { useNotifications } from './Notifications';
import { 
  Settings, User, Bell, Palette, Shield, Globe, Database, 
  Save, Download, Upload, Eye, EyeOff, Check, X, 
  Monitor, Moon, Sun, Smartphone, Tablet, Clock,
  Volume2, VolumeX, Mail, MessageSquare, CreditCard,
  Key, Languages, Calendar, DollarSign, FileText,
  ChevronRight, Info, AlertCircle, Zap, Activity
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { appLogout } from '../lib/logout';

export default function SettingsPage({ useGlobalHeader = false }) {
  const navigate = useNavigate();
  const { preferences, savePreferences, saveProfile, currentUser, setCurrentUser, branches, useDb } = useData();
  const { push } = useNotifications();
  
  // Estado para las pestañas
  const [activeTab, setActiveTab] = useState('perfil');
  const [saving, setSaving] = useState(false);
  
  // Estados para todas las configuraciones
  const [profile, setProfile] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    avatar: '',
    language: 'es',
    timezone: 'America/Mexico_City'
  });
  
  const [appearance, setAppearance] = useState({
    theme: 'system',
    accentColor: '#3B82F6',
    fontSize: 'medium',
    fontFamily: 'system',
    animations: true,
    compactMode: false,
    sidebarCollapsed: false,
    roundedCorners: 'medium'
  });
  
  const [notifications, setNotifications] = useState({
    position: 'top-right',
    maxItems: 4,
    duration: 4000,
    sound: false,
    email: {
      enabled: true,
      frequency: 'instant',
      marketing: false,
      security: true,
      updates: true,
      reports: true
    },
    push: false,
    inApp: true,
    desktop: false
  });
  
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'team',
    showEmail: false,
    showPhone: false,
    activityStatus: true,
    dataExport: true,
    deleteAccount: false
  });
  
  const [regional, setRegional] = useState({
    language: 'es',
    country: 'MX',
    timezone: 'America/Mexico_City',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    currency: 'MXN',
    numberFormat: 'es-MX',
    firstDayOfWeek: 'monday'
  });
  
  const [advanced, setAdvanced] = useState({
    apiAccess: false,
    developerMode: false,
    experimentalFeatures: false,
    performanceMode: 'balanced',
    cacheSize: 'medium',
    autoBackup: true,
    backupFrequency: 'daily',
    dataRetention: 90,
    logLevel: 'info'
  });
  
  const [security, setSecurity] = useState({
    twoFactor: false,
    sessionTimeout: 30,
    ipRestrictions: false,
    allowedIps: [],
    loginNotifications: true,
    suspiciousActivity: true,
    passwordExpiry: 90,
    requireStrongPassword: true
  });

  // OAuth linking and MFA state
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [mfa, setMfa] = useState({
    factors: [],
    enrolling: false,
    enrollData: null,
    totpCode: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const { data: userRes } = await supabase.auth.getUser();
        const identities = userRes?.user?.identities || [];
        setConnectedProviders(identities.map(i => i.provider));
        // TOTP factors
        if (supabase.auth.mfa?.listFactors) {
          const { data } = await supabase.auth.mfa.listFactors();
          const totpFactors = data?.all ?? [];
          setMfa(prev => ({ ...prev, factors: totpFactors }));
          const hasTotp = (data?.totp?.enabled ?? []).length > 0;
          setSecurity(prev => ({ ...prev, twoFactor: hasTotp }));
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const linkProvider = async (provider) => {
    try {
      if (supabase.auth.linkIdentity) {
        await supabase.auth.linkIdentity({ provider });
      } else {
        await supabase.auth.signInWithOAuth({ provider });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('link provider failed', e);
    }
  };

  const enrollTotp = async () => {
    try {
      setMfa(prev => ({ ...prev, enrolling: true }));
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      setMfa(prev => ({ ...prev, enrollData: data, enrolling: false }));
    } catch (e) {
      setMfa(prev => ({ ...prev, enrolling: false }));
    }
  };

  const verifyTotp = async () => {
    try {
      const factorId = mfa.enrollData?.id;
      const code = mfa.totpCode.trim();
      if (!factorId || !code) return;
      const { error } = await supabase.auth.mfa.verify({ factorId, code });
      if (error) throw error;
      setMfa({ factors: [...mfa.factors, mfa.enrollData], enrolling: false, enrollData: null, totpCode: '' });
      setSecurity(prev => ({ ...prev, twoFactor: true }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('verify totp failed', e);
    }
  };

  const disableTotp = async (factorId) => {
    try {
      if (!factorId) return;
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
      setMfa(prev => ({ ...prev, factors: prev.factors.filter(f => f.id !== factorId) }));
      setSecurity(prev => ({ ...prev, twoFactor: false }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('disable totp failed', e);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (currentUser) {
      setProfile(prev => ({
        ...prev,
        name: currentUser.name || '',
        username: currentUser.username || ((currentUser.email || '').split('@')[0] || ''),
        email: currentUser.email || ''
      }));
    }
    
    if (preferences) {
      setAppearance(prev => ({
        ...prev,
        theme: preferences.theme || 'system',
        compactMode: preferences.compact || false
      }));
      
      setNotifications(prev => ({
      ...prev,
        position: preferences.position || 'top-right',
        maxItems: preferences.maxItems || 4,
        duration: preferences.durationMs || 4000
      }));
    }
  }, [currentUser, preferences]);

  const tabs = [
    { id: 'perfil', label: 'Perfil', icon: User },
    { id: 'apariencia', label: 'Apariencia', icon: Palette },
    { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
    { id: 'privacidad', label: 'Privacidad', icon: Eye },
    { id: 'regional', label: 'Regional', icon: Globe },
    { id: 'seguridad', label: 'Seguridad', icon: Shield },
    { id: 'avanzado', label: 'Avanzado', icon: Settings },
    { id: 'datos', label: 'Datos', icon: Database }
  ];

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      // Guardar perfil (nombre)
      const trimmedName = (profile.name || '').trim();
      const trimmedUsername = (profile.username || '').trim();
      if (trimmedName !== (currentUser?.name || '')) {
        await saveProfile?.({ name: trimmedName });
        setCurrentUser && setCurrentUser((prev) => ({ ...(prev || {}), name: trimmedName }));
      }
      if (trimmedUsername && trimmedUsername !== (currentUser?.username || '')) {
        await saveProfile?.({ username: trimmedUsername });
        setCurrentUser && setCurrentUser((prev) => ({ ...(prev || {}), username: trimmedUsername }));
      }
      
      // Guardar preferencias principales
      await savePreferences?.({
        theme: appearance.theme,
        compact: appearance.compactMode,
        position: notifications.position,
        maxItems: notifications.maxItems,
        durationMs: notifications.duration,
        accentColor: appearance.accentColor,
        // Agregar más campos según sea necesario
      });
      
      push('Configuración guardada exitosamente', { type: 'success' });
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      push('Error al guardar la configuración', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const exportSettings = () => {
    const allSettings = {
      profile,
      appearance,
      notifications,
      privacy,
      regional,
      advanced,
      security
    };
    
    const blob = new Blob([JSON.stringify(allSettings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `configuracion-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    push('Configuración exportada', { type: 'success' });
  };

  const importSettings = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const settings = JSON.parse(e.target.result);
        
        if (settings.appearance) setAppearance(settings.appearance);
        if (settings.notifications) setNotifications(settings.notifications);
        if (settings.privacy) setPrivacy(settings.privacy);
        if (settings.regional) setRegional(settings.regional);
        if (settings.advanced) setAdvanced(settings.advanced);
        if (settings.security) setSecurity(settings.security);
        
        push('Configuración importada', { type: 'success' });
      } catch (error) {
        push('Error al importar configuración', { type: 'error' });
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header (hidden when global topbar is used) */}
      {!useGlobalHeader && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Personaliza tu experiencia en MONIPAY
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Importar/Exportar */}
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 hover:dark:bg-gray-600 transition-colors">
                  <Upload className="w-4 h-4" />
                  Importar
                  <input
                    type="file"
                    accept="application/json"
                    onChange={importSettings}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={exportSettings}
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 hover:dark:bg-gray-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Exportar
                </button>
                {/* Guardar */}
                <button
                  onClick={handleSaveAll}
                  disabled={saving}
                  type="button"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
                <button
                  onClick={async () => { await appLogout('/login', { navigate, replace: true }); }}
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 hover:dark:bg-gray-600 transition-colors"
                >
                  <Key className="w-4 h-4" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar de navegación */}
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-[calc(100vh-73px)] neon-border">
          <nav className="p-4 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  type="button"
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:dark:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 p-6">
          {/* Perfil */}
          {activeTab === 'perfil' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Información del Perfil
              </h2>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre completo
                    </label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre de usuario
                </label>
                <input
                  type="text"
                  value={profile.username}
                  onChange={(e) => setProfile({...profile, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Correo electrónico
                    </label>
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({...profile, phone: e.target.value})}
                      placeholder="+52 123 456 7890"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rol
                    </label>
                    <input
                      type="text"
                      value={currentUser?.role || 'Usuario'}
                      disabled
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400"
                    />
                  </div>
                </div>
                
                {/* Biografía eliminada según solicitud */}
              </div>
            </div>
          )}

          {/* Apariencia */}
          {activeTab === 'apariencia' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Personalización de Apariencia
              </h2>
              
              <div className="space-y-6">
                {/* Tema */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Tema
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <button
                      onClick={() => setAppearance({...appearance, theme: 'light'})}
                      type="button"
                      className={`p-4 rounded-lg border-2 transition-all ${
                        appearance.theme === 'light'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Sun className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Claro</p>
                    </button>
                    
                    <button
                      onClick={() => setAppearance({...appearance, theme: 'dark'})}
                      type="button"
                      className={`p-4 rounded-lg border-2 transition-all ${
                        appearance.theme === 'dark'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Moon className="w-6 h-6 mx-auto mb-2 text-gray-700 dark:text-gray-300" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Oscuro</p>
                    </button>
                    
                    <button
                      onClick={() => setAppearance({...appearance, theme: 'system'})}
                      type="button"
                      className={`p-4 rounded-lg border-2 transition-all ${
                        appearance.theme === 'system'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Monitor className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Sistema</p>
                    </button>
                  </div>
          </div>

                {/* Personalización */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Personalización
                  </h3>
                  
          <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Color de acento
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={appearance.accentColor}
                          onChange={(e) => setAppearance({...appearance, accentColor: e.target.value})}
                          className="h-10 w-20"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {appearance.accentColor}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await savePreferences?.({ accentColor: appearance.accentColor });
                              push('Color de acento aplicado', { type: 'success' });
                            } catch (e) {
                              push('No se pudo aplicar el color', { type: 'error' });
                            }
                          }}
                          className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 hover:dark:bg-gray-700 text-xs"
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tamaño de fuente
                      </label>
                      <select
                        value={appearance.fontSize}
                        onChange={(e) => setAppearance({...appearance, fontSize: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="small">Pequeño</option>
                        <option value="medium">Mediano</option>
                        <option value="large">Grande</option>
              </select>
            </div>
                    
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={appearance.animations}
                          onChange={(e) => setAppearance({...appearance, animations: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Activar animaciones
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={appearance.compactMode}
                          onChange={(e) => setAppearance({...appearance, compactMode: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Modo compacto
                        </span>
            </label>
          </div>
                  </div>
                </div>
              </div>
          </div>
          )}

          {/* Notificaciones */}
          {activeTab === 'notificaciones' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Configuración de Notificaciones
              </h2>
              
              <div className="space-y-6">
                {/* Notificaciones en pantalla */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Notificaciones en Pantalla
                  </h3>
                  
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Posición
                      </label>
                      <select
                        value={notifications.position}
                        onChange={(e) => setNotifications({...notifications, position: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                <option value="top-right">Arriba derecha</option>
                <option value="top-left">Arriba izquierda</option>
                <option value="bottom-right">Abajo derecha</option>
                <option value="bottom-left">Abajo izquierda</option>
                        <option value="top-center">Arriba centro</option>
                        <option value="bottom-center">Abajo centro</option>
              </select>
            </div>
                    
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Duración (segundos)
                      </label>
                      <input
                        type="number"
                        value={notifications.duration / 1000}
                        onChange={(e) => setNotifications({...notifications, duration: e.target.value * 1000})}
                        min="1"
                        max="60"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.sound}
                        onChange={(e) => setNotifications({...notifications, sound: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Reproducir sonido
                      </span>
                    </label>
                  </div>
                </div>

                {/* Notificaciones por email */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Notificaciones por Email
                  </h3>
                  
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.email.enabled}
                        onChange={(e) => setNotifications({
                          ...notifications,
                          email: {...notifications.email, enabled: e.target.checked}
                        })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Activar notificaciones por email
                      </span>
                    </label>
                    
                    {notifications.email.enabled && (
                      <div className="ml-7 space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifications.email.security}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: {...notifications.email, security: e.target.checked}
                            })}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Alertas de seguridad
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifications.email.reports}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: {...notifications.email, reports: e.target.checked}
                            })}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Reportes periódicos
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={notifications.email.updates}
                            onChange={(e) => setNotifications({
                              ...notifications,
                              email: {...notifications.email, updates: e.target.checked}
                            })}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Actualizaciones del sistema
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Privacidad */}
          {activeTab === 'privacidad' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Configuración de Privacidad
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Visibilidad del Perfil
                  </h3>
                  
                  <div className="space-y-3">
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        ¿Quién puede ver tu perfil?
                      </label>
                      <select
                        value={privacy.profileVisibility}
                        onChange={(e) => setPrivacy({...privacy, profileVisibility: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="public">Público</option>
                        <option value="team">Solo mi equipo</option>
                        <option value="private">Privado</option>
              </select>
            </div>
                    
                    <div className="space-y-3 mt-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacy.showEmail}
                          onChange={(e) => setPrivacy({...privacy, showEmail: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrar correo electrónico
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacy.showPhone}
                          onChange={(e) => setPrivacy({...privacy, showPhone: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrar número de teléfono
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={privacy.activityStatus}
                          onChange={(e) => setPrivacy({...privacy, activityStatus: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrar estado de actividad
                        </span>
                      </label>
                    </div>
            </div>
          </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Datos y Privacidad
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Exportar mis datos
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Descarga una copia de toda tu información
                        </p>
                      </div>
                      <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                        Exportar
                      </button>
            </div>
                    
                    <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div>
                        <p className="font-medium text-red-900 dark:text-red-200">
                          Eliminar cuenta
                        </p>
                        <p className="text-sm text-red-700 dark:text-red-300">
                          Esta acción es permanente e irreversible
                        </p>
                      </div>
                      <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
            </div>
          </div>
          )}

          {/* Regional */}
          {activeTab === 'regional' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Configuración Regional
              </h2>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Idioma
                    </label>
                    <select
                      value={regional.language}
                      onChange={(e) => setRegional({...regional, language: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                      <option value="pt">Português</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      País
                    </label>
                    <select
                      value={regional.country}
                      onChange={(e) => setRegional({...regional, country: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="MX">México</option>
                      <option value="US">Estados Unidos</option>
                      <option value="ES">España</option>
                      <option value="AR">Argentina</option>
                      <option value="CO">Colombia</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Zona horaria
                    </label>
                    <select
                      value={regional.timezone}
                      onChange={(e) => setRegional({...regional, timezone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="America/Mexico_City">Ciudad de México</option>
                      <option value="America/New_York">Nueva York</option>
                      <option value="America/Los_Angeles">Los Ángeles</option>
                      <option value="Europe/Madrid">Madrid</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Moneda
            </label>
                    <select
                      value={regional.currency}
                      onChange={(e) => setRegional({...regional, currency: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="MXN">Peso Mexicano (MXN)</option>
                      <option value="USD">Dólar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </select>
          </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Formato de fecha
                    </label>
                    <select
                      value={regional.dateFormat}
                      onChange={(e) => setRegional({...regional, dateFormat: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
          </div>
                  
            <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Formato de hora
                    </label>
                    <select
                      value={regional.timeFormat}
                      onChange={(e) => setRegional({...regional, timeFormat: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="24h">24 horas</option>
                      <option value="12h">12 horas (AM/PM)</option>
                    </select>
                  </div>
                </div>
              </div>
          </div>
          )}

          {/* Seguridad */}
          {activeTab === 'seguridad' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Configuración de Seguridad
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Contraseña
                  </h3>
                  
          <div className="space-y-4">
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Contraseña actual
                      </label>
                      <input
                        type="password"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nueva contraseña
                        </label>
                        <input
                          type="password"
                          value={security.newPassword || ''}
                          onChange={(e) => setSecurity({...security, newPassword: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Confirmar contraseña
                        </label>
                        <input
                          type="password"
                          value={security.confirmPassword || ''}
                          onChange={(e) => setSecurity({...security, confirmPassword: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    
                    <button
                      onClick={async () => {
                        const newPass = (security.newPassword || '').trim();
                        const confirm = (security.confirmPassword || '').trim();
                        if (!newPass || newPass.length < 8) { push('La contraseña debe tener al menos 8 caracteres', { type: 'error' }); return; }
                        if (newPass !== confirm) { push('Las contraseñas no coinciden', { type: 'error' }); return; }
                        try {
                          const { error } = await supabase.auth.updateUser({ password: newPass });
                          if (error) throw error;
                          setSecurity(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
                          push('Contraseña actualizada', { type: 'success' });
                        } catch (e) {
                          push(e?.message || 'No se pudo actualizar la contraseña', { type: 'error' });
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                    >
                      Cambiar contraseña
                    </button>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Seguridad Avanzada
                  </h3>
                  
                  <div className="space-y-4">
                    <label className="flex items-center justify-between cursor-pointer">
            <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Autenticación de dos factores
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Añade una capa extra de seguridad a tu cuenta
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={security.twoFactor}
                        onChange={(e) => setSecurity({...security, twoFactor: e.target.checked})}
                        className="w-5 h-5 text-blue-600 rounded"
                      />
                    </label>
                    {/* Providers linking */}
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Proveedores conectados</h4>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => linkProvider('google')}
                          className={`px-3 py-2 rounded border text-sm ${connectedProviders.includes('google') ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'}`}
                        >
                          {connectedProviders.includes('google') ? 'Google conectado' : 'Conectar Google'}
                        </button>
                        <button
                          type="button"
                          onClick={() => linkProvider('facebook')}
                          className={`px-3 py-2 rounded border text-sm ${connectedProviders.includes('facebook') ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200'}`}
                        >
                          {connectedProviders.includes('facebook') ? 'Facebook conectado' : 'Conectar Facebook'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Puedes agregar más servicios soportados por Supabase desde aquí.</p>
                    </div>
                    {/* TOTP enrollment */}
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Autenticación TOTP</h4>
                      {mfa.factors?.length > 0 ? (
                        <div className="space-y-2">
                          {mfa.factors.map((f) => (
                            <div key={f.id} className="flex items-center justify-between p-3 rounded border border-gray-200 dark:border-gray-700">
                              <span className="text-sm text-gray-700 dark:text-gray-300">TOTP habilitado</span>
                              <button onClick={() => disableTotp(f.id)} className="text-sm px-3 py-1 rounded bg-red-600 text-white">Desactivar</button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {!mfa.enrollData ? (
                            <button onClick={enrollTotp} className="px-4 py-2 rounded bg-blue-600 text-white">Configurar TOTP</button>
                          ) : (
                            <div className="space-y-3">
                              {mfa.enrollData.totp?.qr_code && (
                                <img alt="Código QR TOTP" src={mfa.enrollData.totp.qr_code} className="w-40 h-40 border rounded" />
                              )}
                              <input
                                type="text"
                                value={mfa.totpCode}
                                onChange={(e) => setMfa(prev => ({ ...prev, totpCode: e.target.value }))}
                                placeholder="Ingresa el código de tu app"
                                className="w-full md:w-64 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              />
                              <button onClick={verifyTotp} className="px-4 py-2 rounded bg-green-600 text-white">Verificar</button>
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Opcional: puedes activar/desactivar TOTP cuando quieras.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Avanzado */}
          {activeTab === 'avanzado' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Configuración Avanzada
              </h2>
              
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 neon-border">
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          Modo desarrollador
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Activa herramientas de depuración y logs detallados
                        </p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={advanced.developerMode}
                      onChange={(e) => setAdvanced({...advanced, developerMode: e.target.checked})}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rendimiento
                    </label>
                    <select
                      value={advanced.performanceMode}
                      onChange={(e) => setAdvanced({...advanced, performanceMode: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="battery">Ahorro de batería</option>
                      <option value="balanced">Equilibrado</option>
                      <option value="performance">Alto rendimiento</option>
                    </select>
          </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Frecuencia de respaldo
                      </label>
                      <select
                        value={advanced.backupFrequency}
                        onChange={(e) => setAdvanced({...advanced, backupFrequency: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="hourly">Cada hora</option>
                        <option value="daily">Diario</option>
                        <option value="weekly">Semanal</option>
                        <option value="monthly">Mensual</option>
                      </select>
            </div>
                    
            <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Retención de datos (días)
                      </label>
                      <input
                        type="number"
                        value={advanced.dataRetention}
                        onChange={(e) => setAdvanced({...advanced, dataRetention: Number(e.target.value)})}
                        min="30"
                        max="365"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advanced.experimentalFeatures}
                      onChange={(e) => setAdvanced({...advanced, experimentalFeatures: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Activar funciones experimentales
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Datos */}
          {activeTab === 'datos' && (
            <div className="max-w-4xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Gestión de Datos
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Estado del Sistema
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Modo de datos
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {useDb ? 'Base de datos' : 'Local/Demo'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Espacio utilizado
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        125.3 MB
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Último respaldo
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Hace 2 horas
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Versión
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        v2.0.0
                      </span>
            </div>
            </div>
      </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Acciones
                  </h3>
                  
                  <div className="space-y-3">
                    <button className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 hover:dark:bg-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Limpiar caché
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                    
                    <button className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 hover:dark:bg-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <Download className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Crear respaldo
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                    
                    <button className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 hover:dark:bg-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <Activity className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          Ver logs del sistema
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
